var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var RandomNumberGeneratorConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import '@symon-ai/wisepipe-common-data'; // force import for JsonSchemaV2
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { MonteCarloDataAssumptionType } from './monteCarlo/monteCarloAssumption';
import './randomizerColumn'; // force import for JsonSchemaV2
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { randomNumberGeneratorConfigSchemaValidation } from '../validations.generated';
export const randomNumberGeneratorConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:RandomNumberGeneratorConfig',
    type: 'object',
    properties: {
        randomizerColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:RandomizerColumn' },
            uniqueItemProperties: ['name'],
            minItems: 1
        },
        repeatIDColName: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        }
    },
    required: ['randomizerColumns']
};
let RandomNumberGeneratorConfig = RandomNumberGeneratorConfig_1 = class RandomNumberGeneratorConfig extends ToolConfig {
    constructor(randomizerColumns, repeatIDColName, customFormats) {
        super(ToolType.RANDOM_NUMBER_GENERATOR, customFormats);
        this.randomizerColumns = randomizerColumns;
        if (repeatIDColName)
            this.repeatIDColName = repeatIDColName;
        JSON.validate(this, RandomNumberGeneratorConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return RandomNumberGeneratorConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return RandomNumberGeneratorConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return RandomNumberGeneratorConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return RandomNumberGeneratorConfig_1.outs;
    }
    static get in() {
        return RandomNumberGeneratorConfig_1.ins[0];
    }
    static get out() {
        return RandomNumberGeneratorConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.RANDOM_NUMBER_GENERATOR.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.LEARN], this.ins, this.outs, ['randomizer', 'generate', 'distribution', 'uniform', 'normal', 'monte carlo', 'modeling', 'simulation'], false);
    }
    applyParameters(parameters) {
        const baseValues = this.getBaseValues(parameters);
        return new RandomNumberGeneratorConfig_1(parameters.getAppliedVals(this.randomizerColumns), baseValues.repeatIDColName);
    }
    getBaseValues(parameters) {
        return {
            randomizerColumns: this.randomizerColumns.map((c) => c.getBaseValues(parameters)),
            repeatIDColName: parameters.getStrOrUndefVal(this.repeatIDColName)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        if (source) {
            const parameters = iConfigHelper.getParameters();
            const baseValues = this.getBaseValues(parameters);
            exp.copyAll(this.in);
            const commonLearnedDepNames = baseValues.repeatIDColName ? [baseValues.repeatIDColName] : [];
            const learnedCols = baseValues.randomizerColumns.filter((c) => c.assumptionType === MonteCarloDataAssumptionType.LEARNED);
            const dataDistributionCols = baseValues.randomizerColumns.filter((c) => c.assumptionType === MonteCarloDataAssumptionType.DATA_DISTRIBUTION);
            learnedCols.forEach((col) => {
                const groupByCols = col.groupBy ?? [];
                exp.makeNew(new Column(col.name, 'number'), this.getDependents(exp, this.in, [...commonLearnedDepNames, col.learnFrom, ...groupByCols]), true);
            });
            dataDistributionCols.forEach((col) => {
                exp.makeNew(new Column(col.name, 'number'), undefined, true);
            });
            exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source)
            return ToolValidationResult.getMissingSourceColumnsResult();
        const parameters = iConfigHelper.getParameters();
        const baseValues = this.getBaseValues(parameters);
        const columnNames = source.map((col) => col.name);
        const newCols = baseValues.randomizerColumns;
        const newColNames = newCols.map((col) => col.name);
        // new column names must not exist in the source
        if (newColNames.some((name) => columnNames.includes(name))) {
            return ToolValidationResult.getInvalidResult('One or more columns already exist in the source', 'Please rename your randomized columns');
        }
        // new column names must be unique
        if (new Set(newColNames).size !== newColNames.length) {
            return ToolValidationResult.getInvalidResult('One or more columns have the same name', 'Please rename your randomized columns');
        }
        // all learnFrom and groupBy columns must exist in the source
        const learnFromDepCols = newCols
            .filter((c) => c.assumptionType === MonteCarloDataAssumptionType.LEARNED)
            .map((c) => c.learnFrom);
        if (learnFromDepCols.some((col) => !columnNames.includes(col))) {
            return ToolValidationResult.getBadMetadataResult('One or more learnFrom columns do not exist in the source', 'Please check your config');
        }
        const groupByDepCols = newCols
            .filter((c) => c.assumptionType === MonteCarloDataAssumptionType.LEARNED)
            .map((c) => c.groupBy ?? [])
            .flat();
        if (groupByDepCols.some((col) => !columnNames.includes(col))) {
            return ToolValidationResult.getBadMetadataResult('One or more groupBy columns do not exist in the source', 'Please check your config');
        }
        if (baseValues.repeatIDColName && !columnNames.includes(baseValues.repeatIDColName)) {
            return ToolValidationResult.getBadMetadataResult('Repeat ID column does not exist in the source', 'Please check your config');
        }
        return ToolValidationResult.getValidResult();
    }
};
RandomNumberGeneratorConfig = RandomNumberGeneratorConfig_1 = __decorate([
    JsonSchemaV2(randomNumberGeneratorConfigSchema, randomNumberGeneratorConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.RANDOM_NUMBER_GENERATOR)
], RandomNumberGeneratorConfig);
export { RandomNumberGeneratorConfig };
//# sourceMappingURL=randomNumberGeneratorConfig.js.map